import React, { useRef, useState, useEffect } from "react";
import { Tooltip } from "../Tooltip/Tooltip";

interface TruncatedTextTooltipProps {
  text: string;
  // Either a Tailwind max-width CLASS ("max-w-[200px]", the historical form — it may also carry
  // extra classes) or a raw CSS length ("200px", 200, "86%"), which is applied as an inline style.
  // Both are accepted deliberately: the sibling `Tooltip` component takes a raw length for the SAME
  // prop name, so callers reasonably wrote "200px" here too — and because that value used to be
  // interpolated straight into className, it emitted an invalid no-op class and the element silently
  // got NO width bound at all (22 call sites were doing this).
  maxWidth?: string | number;
  className?: string;
  onClick?: () => void;
  children?: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
}

// A single CSS length ("200px", "86%", "1.5rem") rather than a class list. Anchored so a multi-class
// string like "max-w-[160px] text-xs" can never match, and so a bare number is treated as px (same
// as Tooltip does).
const RAW_CSS_LENGTH = /^[\d.]+(px|%|rem|em|vw|vh|ch)?$/;

const isRawCssLength = (value: string | number): boolean =>
  typeof value === "number" || RAW_CSS_LENGTH.test(value.trim());

export const TruncatedTextTooltip: React.FC<TruncatedTextTooltipProps> = ({
  text,
  maxWidth = "max-w-[300px]",
  className = "",
  onClick,
  children,
  side = "top",
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const checkTruncate = () => setIsTruncated(el.scrollWidth > el.clientWidth);

    checkTruncate();
    window.addEventListener("resize", checkTruncate);
    // A window resize alone misses a CSS-transition-driven width change on the element itself —
    // e.g. a hover-triggered padding/width shift (like MetricCard's title making room for the drag
    // grip) can newly truncate text that fit at rest, with no resize event to catch it. A
    // ResizeObserver fires on that box-size change directly, whatever caused it.
    const observer = new ResizeObserver(checkTruncate);
    observer.observe(el);
    return () => {
      window.removeEventListener("resize", checkTruncate);
      observer.disconnect();
    };
    // maxWidth belongs here now that it actually constrains the box: before, a raw length was an
    // inert class, so changing it could never change whether the text overflowed. It can now, and
    // without this dependency the tooltip would keep the stale truncated/not-truncated verdict.
  }, [text, children, maxWidth]);

  // A raw length can't work as a class (Tailwind emits no ".200px" rule), so route it to an inline
  // style and keep it out of className entirely.
  const usesRawLength = isRawCssLength(maxWidth);
  const maxWidthClass = usesRawLength ? "" : String(maxWidth);
  const maxWidthStyle = usesRawLength
    ? { maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : String(maxWidth).trim() }
    : undefined;

  const content = (
    <div
      ref={textRef}
      className={`truncate ${maxWidthClass} ${
        onClick ? "cursor-pointer" : ""
      } ${className}`}
      style={maxWidthStyle}
      onClick={onClick}
    >
      {children || text}
    </div>
  );

  // Always the SAME trigger node, not a conditional mount — a CSS-transition-driven width change
  // (e.g. a hover-triggered padding shift) can flip isTruncated to true WHILE the pointer is still
  // over the element. Swapping the tree (bare div -> Tooltip-wrapped div) at that moment replaces
  // the hovered DOM node, so the browser drops its native :hover state along with any CSS
  // group-hover styling riding on it. Keeping one stable node and only gating whether Radix is
  // ALLOWED to open (via `open={false}` when not truncated) avoids that remount entirely.
  return (
    <Tooltip
      content={text}
      side={side}
      contentClassName="text-md"
      open={isTruncated ? undefined : false}
    >
      {content}
    </Tooltip>
  );
};
